// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Button from "@awc/MobiusOverrides/Button/Button";
import DatePicker, { DatePickerPresentationProps } from "@awc/MobiusOverrides/DatePicker";
import TextField, { TextFieldProps } from "@awc/MobiusOverrides/TextField";
import { XNGAGE_ELEMENTS } from "@awc/XDL/constants";
import { xMask } from "@awc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { DatePickerState } from "@insite/mobius/DatePicker";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import styled from "styled-components";

type Props = WidgetProps & HasProduct;

export interface LeadTimeCalculatorStyles {
    startDatePicker?: DatePickerPresentationProps;
    leadTimePicker?: DatePickerPresentationProps;
    buttonsWrapper?: InjectableCss;
    weeksTextField?: TextFieldProps;
}

export const leadTimeCalculatorStyles: LeadTimeCalculatorStyles = {};

const styles = leadTimeCalculatorStyles;

const StyledForm = styled.form`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const LeadTimeCalculator: React.FC<Props> = () => {
    const [leadTime, setLeadTime] = React.useState<Date>();
    const [startDate, setStartDate] = React.useState<Date>();
    const [weeks, setWeeks] = React.useState<number>();
    const [dateRequired, setDateRequired] = React.useState<boolean>(false);

    const handleSubmit = (event: any) => {
        event.preventDefault();
        setDateRequired(true);
        if (startDate && weeks) {
            const result = new Date(startDate);
            result.setDate(result.getDate() + weeks * 7);
            setLeadTime(result);
        }
    };
    const handleReset = () => {
        setLeadTime(undefined);
        setStartDate(undefined);
        setWeeks(0);
        setDateRequired(false);
    };

    return (
        <StyledForm
            id="leadTimeCalculatorForm"
            onSubmit={handleSubmit}
            data-test-selector="leadTimeCalculatorForm"
            onReset={handleReset}
        >
            <DatePicker
                selectedDay={startDate}
                label={translate("Please Select a Start Day")}
                onDayChange={({ selectedDay }: DatePickerState) => {
                    setStartDate(selectedDay);
                }}
                dateTimePickerProps={{ required: true }}
                {...styles.startDatePicker}
                required={dateRequired}
            />
            <TextField
                value={weeks}
                label={translate("Enter the Number of Weeks")}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const number = Number(xMask.qty(event.target.value));
                    if (Number.isNaN(number)) {
                        return;
                    }
                    setWeeks(number);
                }}
                validateAs="number"
                required={true}
                maxLength={6}
                {...styles.weeksTextField}
            />
            {!!leadTime && (
                <DatePicker
                    label={translate("Your lead time is:")}
                    selectedDay={leadTime}
                    {...styles.leadTimePicker}
                    disabled
                />
            )}
            <StyledWrapper {...styles.buttonsWrapper}>
                <Button type="reset">{translate("Reset")}</Button>
                <Button type="submit">{translate("Calculate")}</Button>
            </StyledWrapper>
        </StyledForm>
    );
};

const widgetModule: WidgetModule = {
    component: LeadTimeCalculator,
    definition: {
        group: XNGAGE_ELEMENTS,
    },
};

export default widgetModule;
